import type { DateFormat } from '@snowball-tech/fractal/dist/src/components/InputDate'

import { getUserLocale } from 'get-user-locale'
import isDate from 'validator/es/lib/isDate'

import isDateInstance from 'lodash/fp/isDate'
import isEmpty from 'lodash/fp/isEmpty'
import isInteger from 'lodash/fp/isInteger'
import isNil from 'lodash/fp/isNil'
import isNumber from 'lodash/fp/isNumber'
import isString from 'lodash/fp/isString'
import padCharsStart from 'lodash/fp/padCharsStart'

const LOCALE = getUserLocale()

export function isValidDate(
  date: string | Date | null | undefined,
): date is Date {
  if (isDateInstance(date)) {
    return !Number.isNaN(date.getTime())
  }

  if (isString(date)) {
    return !isEmpty(date) && isDate(date)
  }

  return false
}

export function shortDateToDateFormat(
  date: string | null | undefined,
): DateFormat {
  if (isEmpty(date) || !date.includes('-')) {
    return { day: '', month: '', year: '' }
  }

  const [yearString, monthString, dayString] = date.split('-')

  const day = Number.parseInt(dayString ?? '', 10)
  const month = Number.parseInt(monthString ?? '', 10)
  const year = Number.parseInt(yearString ?? '', 10)

  return {
    day: !isInteger(day) || day < 1 || day > 31 ? '' : day,
    month: !isInteger(month) || month < 1 || month > 12 ? '' : month,
    year: !isInteger(year) || year < 1900 ? '' : year,
  }
}

export function dateFormatToShortDate(
  dateFormat: DateFormat | null | undefined,
) {
  if (
    isEmpty(dateFormat) ||
    !isNumber(dateFormat.year) ||
    !isInteger(dateFormat.year) ||
    dateFormat.year < 1900 ||
    !isNumber(dateFormat.month) ||
    !isInteger(dateFormat.month) ||
    dateFormat.month < 1 ||
    dateFormat.month > 12 ||
    !isNumber(dateFormat.day) ||
    dateFormat.day < 1 ||
    dateFormat.day > 31 ||
    !isInteger(dateFormat.day)
  ) {
    return ''
  }

  return `${dateFormat.year}-${padCharsStart(
    '0',
    2,
    `${dateFormat.month}`,
  )}-${padCharsStart('0', 2, `${dateFormat.day}`)}`
}

export function isDateFormatValid(date: DateFormat | null | undefined) {
  return (
    !isEmpty(date) &&
    !isNil(date.day) &&
    isNumber(date.day) &&
    isInteger(date.day) &&
    date.day >= 1 &&
    date.day <= 31 &&
    !isNil(date.month) &&
    isNumber(date.month) &&
    isInteger(date.month) &&
    date.month >= 1 &&
    date.month <= 12 &&
    !isNil(date.year) &&
    isNumber(date.year) &&
    isInteger(date.year) &&
    date.year >= 1900 &&
    isDate(
      `${date.year}/${padCharsStart('0', 2, `${date.month}`)}/${padCharsStart(
        '0',
        2,
        `${date.day}`,
      )}`,
    )
  )
}

export function getFormattedDate(date: Date) {
  return new Intl.DateTimeFormat(LOCALE, {
    dateStyle: 'medium',
    timeStyle: 'medium',
  }).format(date)
}
